import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { WorldSelectionSheet } from '@/components/worlds/WorldSelectionSheet';
import { fetchCheckoutSessionStatus, type CheckoutSessionStatus } from '@/features/checkout/api';
import { useCheckoutPolling } from '@/features/credits/useCheckoutPolling';
import { useSubscriptionState } from '@/hooks/useSubscriptionState';
import { phCapture } from '@/lib/analytics/posthog';
import { ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

/**
 * D-10 Bestellung-erfolgreich-Page — `/checkout/success?session_id=...`.
 *
 * Mini-Commit 6.3 fast-path architecture:
 *
 *   1. On mount, fetch `GET /api/checkout/sessions/:sessionId/status`
 *      (Stripe-session SoT). The endpoint is owner-scoped (404 on
 *      ownership mismatch or unknown session — no info leak).
 *   2. If response.status='completed': render success-state directly
 *      using `mode` + `planName` + `packCreditsGranted` from the
 *      response. No polling needed (closes OPS #5 + OPS #8 — webhook
 *      may have already completed before page-mount, especially in
 *      dev with `stripe listen` and on fast networks in production).
 *   3. If response.status='pending': fall through to existing
 *      `useCheckoutPolling` flow. Polling-copy discrimination uses
 *      `response.mode` directly (no more heuristic).
 *   4. If response.status='expired' OR fetch fails (404 / network /
 *      ApiError): render invalid-session-state.
 *
 * Six visual states post-fast-path:
 *
 *   - status-loading:       initial fetch in flight (~50-200ms).
 *   - polling-subscription: status='pending' AND mode='subscription'.
 *   - polling-pack:         status='pending' AND mode='payment'.
 *   - success-subscription: status='completed' AND mode='subscription' OR
 *     polling-detected transition (rare; production webhook-delay).
 *   - success-pack:         status='completed' AND mode='payment' OR
 *     polling-detected transition.
 *   - timeout:              status='pending' → 30s polling timeout.
 *   - invalid-session:      no session_id, status='expired', OR fetch
 *     errored.
 *
 * Primary CTA "Erste Geschichte erstellen" opens the existing
 * WorldSelectionSheet (the bottom-sheet behind the central "+" button
 * on Home). Reuses the existing component verbatim — PWA #39 (cover-
 * SVG swap) is Closing-Commit-7 scope and unrelated.
 */

type FastPathState =
  | { kind: 'loading' }
  | { kind: 'completed'; status: CheckoutSessionStatus }
  | { kind: 'pending'; status: CheckoutSessionStatus }
  | { kind: 'invalid' };

export function CheckoutSuccessRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [fastPath, setFastPath] = useState<FastPathState>(
    sessionId && sessionId.trim() !== '' ? { kind: 'loading' } : { kind: 'invalid' },
  );

  // Fast-path lookup. Fires once on mount when sessionId is present.
  // Status='completed' → success-state directly; 'pending' → polling
  // fallback; 'expired' or fetch error → invalid-session.
  useEffect(() => {
    if (!sessionId || sessionId.trim() === '') return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await fetchCheckoutSessionStatus(sessionId);
        if (cancelled) return;
        if (status.status === 'completed') {
          setFastPath({ kind: 'completed', status });
        } else if (status.status === 'pending') {
          setFastPath({ kind: 'pending', status });
        } else {
          setFastPath({ kind: 'invalid' });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError || err instanceof NetworkError) {
          setFastPath({ kind: 'invalid' });
        } else {
          setFastPath({ kind: 'invalid' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Polling enabled only when fast-path resolved to 'pending'. The
  // hook is always called (Rules of Hooks), but skips network work
  // when `enabled=false`.
  const polling = useCheckoutPolling({ enabled: fastPath.kind === 'pending' });
  const { state: subscriptionState, refetch: refetchSubscriptionState } = useSubscriptionState();
  const [worldSheetOpen, setWorldSheetOpen] = useState(false);

  // On polling-driven success-subscription transition (rare;
  // production webhook-delay window), refetch useSubscriptionState()
  // so the tierLabel interpolation has rich data. Fast-path doesn't
  // need this — the planName comes from the session-status response.
  useEffect(() => {
    if (fastPath.kind === 'pending' && polling.kind === 'success-subscription') {
      void refetchSubscriptionState();
    }
  }, [fastPath.kind, polling.kind, refetchSubscriptionState]);

  // Funnel: subscription_active fires once on the activation transition. Both
  // the fast-path (session status 'completed' + subscription) and the rare
  // polling transition resolve here; dedupe per session_id so a success-page
  // refresh (an "already subscribed" load) never re-fires. The dedupe key is
  // only written once the event actually emits (phCapture no-ops pre-consent).
  useEffect(() => {
    const subscriptionSucceeded =
      (fastPath.kind === 'completed' && fastPath.status.mode === 'subscription') ||
      polling.kind === 'success-subscription';
    if (!subscriptionSucceeded || !sessionId) return;
    const dedupeKey = `dreamable_subscription_active_${sessionId}`;
    try {
      if (sessionStorage.getItem(dedupeKey)) return;
    } catch {
      // sessionStorage unavailable — fall through; phCapture gating still applies
    }
    const plan =
      fastPath.kind === 'completed' ? fastPath.status.planName : subscriptionState?.tierLabel;
    const emitted = phCapture('subscription_active', plan ? { plan } : undefined);
    if (emitted) {
      try {
        sessionStorage.setItem(dedupeKey, '1');
      } catch {
        // best-effort dedupe persistence
      }
    }
  }, [fastPath, polling.kind, sessionId, subscriptionState?.tierLabel]);

  const S = STRINGS.checkout.success;

  // Invalid session — short-circuits before any polling logic runs.
  if (fastPath.kind === 'invalid') {
    return (
      <InvalidSessionBlock
        onCta={() => navigate('/')}
        heading={S.invalidSession.heading}
        subtext={S.invalidSession.subtext}
        ctaLabel={S.invalidSession.cta}
      />
    );
  }

  // Loading — brief in-flight window before fast-path resolves.
  // Render the polling-loading visual with neutral copy (we don't
  // yet know whether it's sub vs pack until status response lands).
  if (fastPath.kind === 'loading') {
    return (
      <CheckoutSuccessShell
        heading={S.polling.headingSubscription}
        subtext={S.polling.subtext}
        loading
        testId="checkout-success-polling"
      />
    );
  }

  // Fast-path completed — render success-state directly using the
  // session-status response. mode is authoritative (no heuristic).
  if (fastPath.kind === 'completed') {
    if (fastPath.status.mode === 'subscription') {
      const subscriptionDetail = S.success.subscriptionDetail.replace(
        '{planName}',
        fastPath.status.planName,
      );
      return (
        <SuccessShell
          detail={subscriptionDetail}
          navigate={navigate}
          worldSheetOpen={worldSheetOpen}
          setWorldSheetOpen={setWorldSheetOpen}
          testId="checkout-success-subscription"
        />
      );
    }
    // mode === 'payment' (pack)
    const packDetail = S.success.packDetail.replace(
      '{count}',
      String(fastPath.status.packCreditsGranted ?? 0),
    );
    return (
      <SuccessShell
        detail={packDetail}
        navigate={navigate}
        worldSheetOpen={worldSheetOpen}
        setWorldSheetOpen={setWorldSheetOpen}
        testId="checkout-success-pack"
      />
    );
  }

  // fastPath.kind === 'pending' — fall through to existing polling
  // flow. Mode discrimination for polling-copy comes from the
  // session-status response (no more heuristic).
  if (polling.kind === 'polling') {
    return (
      <CheckoutSuccessShell
        heading={
          fastPath.status.mode === 'subscription'
            ? S.polling.headingSubscription
            : S.polling.headingPack
        }
        subtext={S.polling.subtext}
        loading
        testId="checkout-success-polling"
      />
    );
  }

  if (polling.kind === 'success-subscription') {
    const tierLabel = subscriptionState?.tierLabel ?? fastPath.status.planName;
    const subscriptionDetail = S.success.subscriptionDetail.replace('{planName}', tierLabel);
    return (
      <SuccessShell
        detail={subscriptionDetail}
        navigate={navigate}
        worldSheetOpen={worldSheetOpen}
        setWorldSheetOpen={setWorldSheetOpen}
        testId="checkout-success-subscription"
      />
    );
  }

  if (polling.kind === 'success-pack') {
    const packDetail = S.success.packDetail.replace('{count}', String(polling.packCreditsGranted));
    return (
      <SuccessShell
        detail={packDetail}
        navigate={navigate}
        worldSheetOpen={worldSheetOpen}
        setWorldSheetOpen={setWorldSheetOpen}
        testId="checkout-success-pack"
      />
    );
  }

  // polling.kind === 'timeout'
  return (
    <CheckoutSuccessShell
      heading={S.timeout.heading}
      subtext={S.timeout.subtext}
      cta={{ label: S.timeout.cta, onClick: () => navigate('/') }}
      testId="checkout-success-timeout"
    />
  );
}

// ── Route-private shell components ─────────────────────────

interface CheckoutSuccessShellProps {
  heading: string;
  subtext?: string;
  loading?: boolean;
  cta?: { label: string; onClick: () => void };
  testId?: string;
}

/**
 * Shared layout for polling / timeout / invalid-session states.
 * Centered card with optional spinner + optional CTA.
 */
function CheckoutSuccessShell({
  heading,
  subtext,
  loading,
  cta,
  testId,
}: CheckoutSuccessShellProps) {
  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div
          className="flex min-h-screen flex-col items-center justify-center gap-4 py-10 desktop:mx-auto desktop:max-w-[640px]"
          data-testid={testId}
        >
          {loading && (
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-dream-gold border-t-transparent"
              role="status"
              aria-label="loading"
            />
          )}
          <h1 className="text-center text-base font-semibold leading-5 text-dream-navy">
            {heading}
          </h1>
          {subtext && (
            <p className="max-w-md text-center text-xs leading-relaxed text-dream-navy/70">
              {subtext}
            </p>
          )}
          {cta && (
            <button
              type="button"
              onClick={cta.onClick}
              className="mt-2 flex w-full max-w-xs items-center justify-center rounded-lg border border-dream-navy/40 bg-transparent px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy/70 transition-colors hover:bg-dream-navy/5"
            >
              {cta.label}
            </button>
          )}
        </div>
      </ContentContainer>
    </div>
  );
}

interface InvalidSessionBlockProps {
  heading: string;
  subtext: string;
  ctaLabel: string;
  onCta: () => void;
}

function InvalidSessionBlock({ heading, subtext, ctaLabel, onCta }: InvalidSessionBlockProps) {
  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div
          className="flex min-h-screen flex-col items-center justify-center gap-4 py-10 desktop:mx-auto desktop:max-w-[640px]"
          data-testid="checkout-success-invalid-session"
        >
          <h1 className="text-center text-base font-semibold leading-5 text-dream-navy">
            {heading}
          </h1>
          <p className="max-w-md text-center text-xs leading-relaxed text-dream-navy/70">
            {subtext}
          </p>
          <button
            type="button"
            onClick={onCta}
            className="mt-2 flex w-full max-w-xs items-center justify-center rounded-lg border border-dream-navy/40 bg-transparent px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy/70 transition-colors hover:bg-dream-navy/5"
          >
            {ctaLabel}
          </button>
        </div>
      </ContentContainer>
    </div>
  );
}

interface SuccessShellProps {
  detail: string;
  navigate: ReturnType<typeof useNavigate>;
  worldSheetOpen: boolean;
  setWorldSheetOpen: (open: boolean) => void;
  testId: string;
}

/**
 * Shared layout for success-subscription / success-pack states. Same
 * heading + cta-line + primary/secondary CTAs; only the `detail` line
 * varies (subscription planName interpolation vs pack count).
 */
function SuccessShell({
  detail,
  navigate,
  worldSheetOpen,
  setWorldSheetOpen,
  testId,
}: SuccessShellProps) {
  const S = STRINGS.checkout.success.success;
  return (
    <>
      <div className="min-h-screen bg-dream-white">
        <ContentContainer>
          <div
            className="flex min-h-screen flex-col items-center justify-center gap-4 py-10 desktop:mx-auto desktop:max-w-[640px]"
            data-testid={testId}
          >
            <h1 className="text-center text-xl font-semibold leading-7 text-dream-navy">
              {S.heading}
            </h1>
            <p
              className="max-w-md text-center text-sm leading-relaxed text-dream-navy"
              data-testid={`${testId}-detail`}
            >
              {detail}
            </p>
            <p className="max-w-md text-center text-xs leading-relaxed text-dream-navy/70">
              {S.cta}
            </p>
            <button
              type="button"
              onClick={() => setWorldSheetOpen(true)}
              className="mt-2 flex w-full max-w-xs items-center justify-center overflow-hidden rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy transition-colors hover:bg-dream-gold/90"
              data-testid={`${testId}-cta`}
            >
              {S.ctaButton}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-xs font-medium text-dream-navy/60 underline decoration-solid"
              data-testid={`${testId}-secondary-cta`}
            >
              {S.ctaSecondary}
            </button>
          </div>
        </ContentContainer>
      </div>
      <WorldSelectionSheet open={worldSheetOpen} onOpenChange={setWorldSheetOpen} />
    </>
  );
}
